import { useEffect, useState } from 'react';
import { Alert, Button } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ACCESS_ROLES, NAV } from 'src/config';
import { ACCOUNT_BILLING_INFORMATION_PATH } from 'src/routes/config';
import { useAuthentication } from 'src/features/authentication/context';
import {
  useGetBillingStatusQuery,
  BILLING_INFORMATION_STATUS_QUERY_KEY,
} from 'src/hooks/reactQueryApiHooks';
import { LocalStorageService } from 'src/features/authentication/utils/LocalStorageService';

const DISMISS_DURATION_MS = 1000 * 60 * 60 * 24; // 24 hours
// const DISMISS_DURATION_MS = 1000 * 60 * 5; // 5 min (dev)

const RECHECK_INTERVAL_MS = 1000 * 60 * 15; // 15 min
// const RECHECK_INTERVAL_MS = 1000 * 3; // dev

const getStorageKey = (customerId: string, userId: string) =>
  `billing_alert_dismissed_${customerId}_${userId}`;

const isDismissalActive = (customerId: string, userId: string): boolean => {
  if (customerId === '' || userId === '') {
    return true;
  }

  try {
    const rawTimestamp = LocalStorageService.getRaw(getStorageKey(customerId, userId));

    if (!rawTimestamp) {
      return false;
    }

    const active = Date.now() - Number(rawTimestamp) < DISMISS_DURATION_MS;

    if (!active) {
      LocalStorageService.remove(getStorageKey(customerId, userId));
    }

    return active;
  } catch {
    return false;
  }
};

export const BillingAlert = () => {
  const {
    user,
    getCurrentRole,
    customerId: { value: customerId },
    isBillingAdminForCustomer,
  } = useAuthentication();
  const queryClient = useQueryClient();

  const validCustomerId = customerId ?? '';
  const validUserId = user?.userId ?? '';
  const currentRole = getCurrentRole(validCustomerId, null);

  const isBillingAdmin = isBillingAdminForCustomer(validCustomerId);
  const isAccountAdmin = currentRole === ACCESS_ROLES.ACCOUNT_ADMIN;

  const canSeeBillingAlert = isBillingAdmin || isAccountAdmin;

  const { billingInformationStatus, dataUpdatedAt } = useGetBillingStatusQuery({
    customerId: validCustomerId,
    enabled: canSeeBillingAlert,
  });

  const [isDismissed, setIsDismissed] = useState<boolean>(() =>
    isDismissalActive(validCustomerId, validUserId)
  );

  useEffect(() => {
    if (validCustomerId === '' || validUserId === '') {
      return;
    }

    setIsDismissed(isDismissalActive(validCustomerId, validUserId));
  }, [validCustomerId, validUserId, dataUpdatedAt]);

  const handleDismiss = () => {
    LocalStorageService.set(getStorageKey(validCustomerId, validUserId), String(Date.now()));
    setIsDismissed(true);
  };

  useEffect(() => {
    if (!validCustomerId || !validUserId) {
      return;
    }

    if (billingInformationStatus?.billingConfigured !== false) {
      return;
    }

    const interval = setInterval(() => {
      const active = isDismissalActive(validCustomerId, validUserId);

      if (!active) {
        queryClient.refetchQueries({
          queryKey: [BILLING_INFORMATION_STATUS_QUERY_KEY, validCustomerId],
        });
      }
    }, RECHECK_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [validCustomerId, validUserId, billingInformationStatus?.billingConfigured, queryClient]);

  const showAlert = () => {
    if (isDismissed) {
      return false;
    }

    // NOTE: The order matters because this is a special case
    if (isBillingAdmin) {
      if (billingInformationStatus === undefined) {
        return false;
      }

      if (billingInformationStatus.billingConfigured) {
        return false;
      } else {
        return true;
      }
    }

    if (isAccountAdmin) {
      if (billingInformationStatus === undefined) {
        return false;
      }

      if (billingInformationStatus.hasBillingAdmin) {
        return false;
      } else {
        return true;
      }
    }

    return false;
  };

  const alertMessage = () => {
    // NOTE: The order matters because this is a special case
    if (isBillingAdmin) {
      return 'Please enter your billing information.';
    }

    if (isAccountAdmin) {
      return 'Your account setup is incomplete. Please assign a Billing Admin to continue.';
    }
  };

  if (!showAlert()) return null;

  return (
    <Alert
      severity="warning"
      action={
        <>
          <Button color="inherit" size="small" onClick={handleDismiss} sx={{ mr: 1 }}>
            Dismiss
          </Button>
          {isBillingAdmin && (
            <Button
              component={Link}
              to={`${ACCOUNT_BILLING_INFORMATION_PATH}?customerId=${validCustomerId}`}
              color="inherit"
              size="small"
              variant="outlined"
              sx={{ borderColor: 'warning.dark', whiteSpace: 'nowrap' }}
            >
              Go To Billing
            </Button>
          )}
        </>
      }
      sx={(theme) => ({
        position: 'fixed',
        top: 20,
        left: { xs: '50%', md: `calc(50% + ${NAV.W_SIDE_BAR / 2}px)` },
        transform: 'translateX(-50%)',
        width: { xs: 'calc(100% - 32px)', md: `calc(100vw - ${NAV.W_SIDE_BAR}px - 32px)` },
        maxWidth: 'md',
        zIndex: 'appBar',
        border: `1px solid ${alpha(theme.palette.warning.main, 0.5)}`,
        justifyContent: 'center',
        alignItems: 'center',
        '& .MuiAlert-message': { flex: '0 1 auto' },
        '& .MuiAlert-action': {
          alignItems: 'center',
          [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
            alignItems: 'stretch',
            gap: 1,
          },
        },
      })}
    >
      {alertMessage()}
    </Alert>
  );
};
